import {
  Typography,
  makeStyles,
  Container,
  TextField,
  Grid,
  Chip,
  Button,
} from '@material-ui/core'
import { Select } from '@material-ui/core'
import { MenuItem } from '@material-ui/core'
import { InputAdornment } from '@material-ui/core'
import { IconButton } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import Fuse from 'fuse.js'
import { chain, isArray, isEmpty, isString, sortBy } from 'lodash'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import React, { useEffect, useState } from 'react'

import ButtonLink from '@aletheia/common/components/ButtonLink'
import Link from '@aletheia/common/components/Link'
import Page from '@aletheia/common/components/Page'

import { FilterField } from '../../components/Grants/FilterField'
import { grantsDB } from '../../lib/grantsDB'

export const enableTags = false

export type Grant = {
  id: string
  fund: string
  description: string
  amount: number
  grantee: string
  round: string
  published: boolean
  highlighted: boolean
  tags: string[]
}

const formatDollars = (val: number) =>
  Number(val as number).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })

type Filter = {
  search: string
  fund: string[]
  round: string[]
  grantee: string[]
  tag: string[]
  amount: string[]
  sort: sortKey
}

// FILTER encode/decode as url query
const arrayDelimiter = '|'

const filterToParams = (f: Filter) => {
  const cleaned = chain(f)
    .omitBy(isEmpty)
    .mapValues((v) =>
      v && isArray(v) ? v.map(encodeURIComponent).join(arrayDelimiter) : v,
    )
    .value()
  return new URLSearchParams(cleaned)
}

const decodeArray = (field: string | string[] | undefined) => {
  if (isString(field)) {
    return field.split(arrayDelimiter).map(decodeURIComponent)
  } else {
    return []
  }
}

const filterFromQuery = (
  q: ParsedUrlQuery,
  roundOptions: string[],
  granteeOptions: string[],
  tagOptions: string[],
) => {
  return {
    search: isString(q.search) ? q.search : '',
    fund: validateFilter(decodeArray(q.fund), Object.keys(fundSlugs)),
    round: validateFilter(decodeArray(q.round), roundOptions),
    grantee: validateFilter(decodeArray(q.grantee), granteeOptions),
    tag: validateFilter(decodeArray(q.tag), tagOptions),
    amount: validateFilter(decodeArray(q.amount), Object.keys(amountOptions)),
    sort: (isString(q.sort) &&
    Object.values(sortOptions).includes(q.sort as sortKey)
      ? q.sort
      : 'round') as sortKey,
  }
}

const validateFilter = (fs: string[], validValues: string[]) => {
  return fs.filter((f) => {
    return validValues.includes(f)
  })
}

// FILTER OPTIONS
export const fundSlugs: Record<string, string> = {
  'Long-Term Future Fund': 'far-future',
  'EA Infrastructure Fund': 'ea-community',
  'Animal Welfare Fund': 'animal-welfare',
  'Global Health and Development Fund': 'global-development',
}

// option label => comparison function for value
const amountOptions: Record<string, (x: number) => boolean> = {
  '$0-$10k': (x: number) => x <= 10000,
  '$10k-$100k': (x: number) => x > 10000 && x <= 100000,
  '$100k+': (x: number) => x > 100000,
}

// sort variables
type sortKey = 'round' | 'amount' | 'grantee'
const defaultSort: sortKey = 'round'

// option label => sortKey
const sortOptions: Record<string, sortKey> = {
  'Funding Date': 'round',
  Amount: 'amount',
  Grantee: 'grantee',
}

const granteeSortFn = (g: Grant) => {
  if (g.grantee === '(Anonymous)') {
    // force anonymous to the end
    return 'zzz'
  } else {
    // normalize converts diacritics e.g. Č to C
    return g.grantee.normalize('NFD')
  }
}

const useStyles = makeStyles((theme) => ({
  searchBar: {
    zIndex: 300,
    background: theme.palette.primary.light,
    boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
    [theme.breakpoints.up('md')]: {
      position: 'sticky',
      alignSelf: 'flex-start',
      height: 'auto',
      top: 72,
    },
  },
  searchBox: {
    width: 300,
    background: 'white',
    margin: theme.spacing(2, 4, 2, 0),
  },
  sortBy: {
    margin: theme.spacing(2, 0),
  },
  grantWrap: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      marginRight: theme.spacing(6),
    },
  },
  grant: {
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '&:not(:last-child)': {
      borderBottom: `1px dashed ${theme.palette.secondary.light}`,
    },
    [theme.breakpoints.up('md')]: {
      paddingBottom: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
  },
  grantRound: {
    fontSize: '0.75rem',
    color: theme.palette.secondary.dark,
  },
  grantee: {
    fontWeight: 700,
    color: theme.palette.primary.dark,
    display: 'inline-block',
  },
  grantAmount: {
    fontWeight: 700,
    display: 'inline-block',
  },
  grantDescription: {
    marginTop: theme.spacing(1),
  },
  fundWrap: {
    order: -1, // fund chip goes above grantee on mobile
    margin: theme.spacing(0.5, 0),
    [theme.breakpoints.up('sm')]: {
      order: 1,
      textAlign: 'right',
      margin: 0,
    },
  },
  fundChip: {
    cursor: 'pointer',
  },
  filters: {
    marginTop: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      position: 'sticky',
      alignSelf: 'flex-start',
      height: 'auto',
      top: 200,
      order: 2, // filters panel on the right for big screens
    },
  },
  filterMenuItem: {
    whiteSpace: 'unset',
    wordBreak: 'break-all',
  },
  tagBlock: {
    marginTop: theme.spacing(1),
  },
  tag: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    maxWidth: '100%',
  },
}))

type GrantsDatabaseProps = {
  GrantsList: Grant[]
  roundOptions: string[]
  granteeOptions: string[]
  tagOptions: string[]
}

const GrantsDatabase: React.FC<GrantsDatabaseProps> = ({
  GrantsList,
  roundOptions,
  granteeOptions,
  tagOptions,
}) => {
  const classes = useStyles()
  const router = useRouter()
  const [fuse, setFuse] = useState<Fuse<Grant> | undefined>()
  const [visibleGrants, setVisibleGrants] = useState<Grant[]>([])
  const [showAll, setShowAll] = useState<boolean>(false)
  const [hiddenAmount, setHiddenAmount] = useState<number>(0)
  const [filter, setFilter] = useState<Filter>(
    filterFromQuery(router.query, roundOptions, granteeOptions, tagOptions),
  )

  // build Fuse for component state
  useEffect(() => {
    setFuse(
      new Fuse(GrantsList, {
        keys: ['fund', 'description', 'grantee', 'round'],
        threshold: 0.2,
      }),
    )
  }, [GrantsList])

  // change url when filters change
  // intentionally NOT using router.replace(), as it triggers re-render of the component on URL change
  // that conflicts with our `filter` useEffect dependency causing multiple renders on each filter change
  useEffect(() => {
    const params = filterToParams(filter).toString()
    if (params !== '' && window && window.location.search !== params) {
      const newPath = window.location.pathname + '?' + params
      window.history.replaceState(
        { ...window.history.state, as: newPath, url: newPath },
        '',
        newPath,
      )
    }
  }, [filter])

  // update visible grants when filters change
  useEffect(() => {
    // do fuse filter if there is more than one character, and the fuse index exists
    let grants =
      fuse && filter.search !== '' && filter.search.length > 1
        ? fuse.search(filter.search).map((result) => result.item)
        : GrantsList

    // then filter by various multi-selects
    if (filter.fund.length > 0) {
      grants = grants.filter((g) => {
        return filter.fund.includes(g.fund)
      })
    }

    if (filter.round.length > 0) {
      grants = grants.filter((g) => {
        return filter.round.includes(g.round)
      })
    }

    if (filter.grantee.length > 0) {
      grants = grants.filter((g) => {
        return filter.grantee.includes(g.grantee)
      })
    }

    if (filter.amount.length > 0) {
      grants = grants.filter((g) => {
        return filter.amount.some((amount) => {
          // Array.some stops checking as soon as one returns true
          const checkFunc = amountOptions[amount]
          if (checkFunc) {
            return checkFunc(g.amount)
          } else {
            return false
          }
        })
      })
    }

    if (filter.tag.length > 0) {
      grants = grants.filter((g) => {
        if (!isArray(g.tags)) {
          return false
        }
        return filter.tag.every((t) => {
          // Array.every means we must match *all* tags
          return g.tags.includes(t)
        })
      })
    }

    // finally sort the list
    const sort = filter.sort || defaultSort
    switch (sort) {
      case 'grantee':
        grants = sortBy(grants, [granteeSortFn, (g) => -g.amount])
        break
      case 'round':
        grants = sortBy(grants, ['round', 'amount']).reverse()
        break
      case 'amount':
        grants = sortBy(grants, [(g) => -g.amount, granteeSortFn])
    }

    if (!showAll) {
      setHiddenAmount(grants.length - 6)
      grants = grants.slice(0, 6)
    } else {
      setHiddenAmount(0)
    }

    setVisibleGrants(grants)
  }, [GrantsList, showAll, fuse, filter])

  return (
    <Page title={'Grants Database'} variant="full">
      <Container>
        <Typography component="div">
          <p>
            This database lists grants we{' '}
            <Link href="/grantmaking-approach">have recommended</Link> in the
            past. <br />
            If you are interested in applying for a grant,{' '}
            <Link href="/apply-for-funding">read this page</Link>.
          </p>
          <p>
            <Alert severity="info">
              Please note that this page is in beta testing and grant data may
              not be accurate
            </Alert>
          </p>
        </Typography>
      </Container>

      <div className={classes.searchBar}>
        <Container>
          <Grid container>
            <Grid item xs={12} sm={8}>
              <TextField
                className={classes.searchBox}
                label="Search"
                variant="outlined"
                name="search"
                value={filter.search}
                onChange={(event) =>
                  setFilter({ ...filter, search: event.target.value })
                }
                InputProps={{
                  endAdornment: filter.search.length > 0 && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="erase search"
                        onClick={() => {
                          setFilter({ ...filter, search: '' })
                        }}
                        edge="end"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <div className={classes.sortBy}>
                Sort by:&nbsp;
                <Select
                  value={filter.sort}
                  variant="outlined"
                  style={{ background: 'white' }}
                  onChange={(event) =>
                    setFilter({
                      ...filter,
                      sort: String(event.target.value) as sortKey,
                    })
                  }
                >
                  {Object.keys(sortOptions).map((label) => {
                    const key = sortOptions[label]
                    return (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    )
                  })}
                </Select>
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>

      <Container>
        <Grid container>
          <Grid item xs={12} md={4} className={classes.filters}>
            <FilterField
              label="EA Fund"
              options={Object.keys(fundSlugs)}
              defaultValue={filter.fund}
              onChange={(event, value) => {
                setFilter({ ...filter, fund: value })
              }}
            />
            <FilterField
              label="Funding Date"
              options={roundOptions}
              defaultValue={filter.round}
              onChange={(event, value) => {
                setFilter({ ...filter, round: value })
              }}
            />
            <FilterField
              label="Grantee"
              options={granteeOptions}
              defaultValue={filter.grantee}
              onChange={(event, value) => {
                setFilter({ ...filter, grantee: value })
              }}
            />
            <FilterField
              label="Grant Size"
              options={Object.keys(amountOptions)}
              defaultValue={filter.amount}
              onChange={(event, value) => {
                setFilter({ ...filter, amount: value })
              }}
            />
            {enableTags && (
              <FilterField
                label="Tags"
                options={tagOptions}
                defaultValue={filter.tag}
                onChange={(event, value) => {
                  setFilter({ ...filter, tag: value })
                }}
              />
            )}

            <ButtonLink
              fullWidth
              variant="outlined"
              color="primary"
              href="/api/grants"
              target="_blank"
              style={{ marginBottom: '1rem' }}
            >
              Download Grants Database CSV
            </ButtonLink>

            <ButtonLink
              fullWidth
              variant="outlined"
              color="secondary"
              href="https://nsvzq486.paperform.co/"
              target="_blank"
            >
              Provide Grants Database Feedback
            </ButtonLink>
          </Grid>
          <Grid item xs={12} md={8}>
            <div className={classes.grantWrap}>
              {visibleGrants.length === 0 && (
                <Alert severity="warning">
                  No grants match your search criteria.
                </Alert>
              )}
              {visibleGrants.map((g) => (
                <GrantRow key={g.id} grant={g} />
              ))}
              {hiddenAmount > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowAll(true)
                  }}
                >
                  Show {hiddenAmount} more grants
                </Button>
              )}
            </div>
          </Grid>
        </Grid>
      </Container>
    </Page>
  )
}

export const GrantRow = ({
  grant,
  omitFundLabel = false,
}: {
  grant: Grant
  omitFundLabel?: boolean
}) => {
  const classes = useStyles()

  return (
    <div className={classes.grant}>
      <div className={classes.grantRound}>{grant.round}</div>
      <Grid container>
        <Grid item xs={12} sm={8}>
          <Typography variant="h6" className={classes.grantee}>
            {grant.grantee}&nbsp;-&nbsp;
          </Typography>
          <Typography variant="h6" className={classes.grantAmount}>
            {formatDollars(grant.amount)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} className={classes.fundWrap}>
          {!omitFundLabel && (
            <Chip
              className={classes.fundChip}
              component="a"
              href={`/funds/${fundSlugs[grant.fund]}`}
              variant="outlined"
              color="primary"
              label={grant.fund}
            />
          )}
        </Grid>
      </Grid>
      <Typography variant="body2" className={classes.grantDescription}>
        {grant.description}
      </Typography>

      {enableTags && grant.tags && grant.tags.length > 0 && (
        <div className={classes.tagBlock}>
          {grant.tags.map((t) => (
            <Chip
              className={classes.tag}
              key={t}
              variant="outlined"
              size="small"
              label={t}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default GrantsDatabase

export const getStaticProps: GetStaticProps = async () => {
  const GrantsList = await grantsDB()

  return {
    props: {
      GrantsList,
      roundOptions: chain(GrantsList)
        .map((g) => g.round)
        .uniq()
        .value(),
      granteeOptions: chain(GrantsList)
        .sortBy(granteeSortFn)
        .map((g) => g.grantee)
        .uniq()
        .value(),
      tagOptions: chain(GrantsList)
        .flatMap((g) => g.tags)
        .compact()
        .uniq()
        .sort()
        .value(),
    },
    revalidate: 60,
  }
}
